import { Suspense } from 'react';
import { lazyWithRetry as lazy } from '@/lib/lazyWithRetry';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { PageTransition } from './PageTransition';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { AppLayout } from './AppLayout';
import { Loader2 } from 'lucide-react';

// Lazy-loaded pages — admin
const Clients = lazy(() => import('@/pages/Clients'));
const ClientDetail = lazy(() => import('@/pages/ClientDetail'));
const Loans = lazy(() => import('@/pages/Loans'));
const LoanDetail = lazy(() => import('@/pages/LoanDetail'));
const Payments = lazy(() => import('@/pages/Payments'));
const Settings = lazy(() => import('@/pages/Settings'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const SystemTodos = lazy(() => import('@/pages/SystemTodos'));
const SystemHealth = lazy(() => import('@/pages/SystemHealth'));
const AuditLog = lazy(() => import('@/pages/AuditLog'));
const Finance = lazy(() => import('@/pages/Finance'));
const Financials = lazy(() => import('@/pages/Financials'));
const Portfolio = lazy(() => import('@/pages/Portfolio'));
const DecisionAudit = lazy(() => import('@/pages/DecisionAudit'));
const DecisionSandbox = lazy(() => import('@/pages/DecisionSandbox'));
const AiAnalysisSandbox = lazy(() => import('@/pages/AiAnalysisSandbox'));
const BankTransactions = lazy(() => import('@/pages/BankTransactions'));
const RulesManagement = lazy(() => import('@/pages/RulesManagement'));

// Lazy-loaded pages — capital module
const CapitalOverview = lazy(() => import('@/pages/capital/CapitalOverview'));
const CapitalSources = lazy(() => import('@/pages/capital/CapitalSources'));
const CapitalSourceDetail = lazy(() => import('@/pages/capital/CapitalSourceDetail'));
const CapitalInvestments = lazy(() => import('@/pages/capital/CapitalInvestments'));
const InvestorPortal = lazy(() => import('@/pages/capital/InvestorPortal'));

// Lazy-loaded pages — user
const UserDashboard = lazy(() => import('@/pages/UserDashboard'));
const UserProfile = lazy(() => import('@/pages/UserProfile'));
const MyLoans = lazy(() => import('@/pages/MyLoans'));
const NewLoanRequest = lazy(() => import('@/pages/NewLoanRequest'));
const ContractSign = lazy(() => import('@/pages/ContractSign'));

// Lazy-loaded pages — public
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const GuestApplication = lazy(() => import('@/pages/GuestApplication'));
const Login = lazy(() => import('@/pages/Login'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Cookies = lazy(() => import('@/pages/Cookies'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// Lazy-loaded pages — process
const Intake = lazy(() => import('@/pages/process/Intake'));
const Scoring = lazy(() => import('@/pages/process/Scoring'));
const Approval = lazy(() => import('@/pages/process/Approval'));
const Contracts = lazy(() => import('@/pages/process/Contracts'));

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  if (authLoading || roleLoading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/profile" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function UserRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { loading: roleLoading } = useUserRole();
  if (authLoading || roleLoading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function ProfileRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  if (authLoading || roleLoading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (isAdmin) return <Navigate to="/settings" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function InvestorRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isInvestor, loading: roleLoading } = useUserRole();
  if (authLoading || roleLoading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isInvestor) return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function RoleBasedRedirect() {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isInvestor, loading: roleLoading } = useUserRole();
  if (authLoading || roleLoading) return <PageLoader />;
  if (!user) return <LandingPage />;
  if (isAdmin) return <Navigate to="/finance" replace />;
  if (isInvestor) return <Navigate to="/investor-portal" replace />;
  return <Navigate to="/user-dashboard" replace />;
}

export function AnimatedRoutes() {
  const location = useLocation();

  return (
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<RoleBasedRedirect />} />

          {/* Public routes */}
          <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
          <Route path="/cookies" element={<PageTransition><Cookies /></PageTransition>} />
          <Route path="/guest-application" element={<PageTransition><GuestApplication /></PageTransition>} />
          <Route path="/web" element={<PageTransition><LandingPage /></PageTransition>} />

          {/* Process routes */}
          <Route path="/process/intake" element={<AdminRoute><PageTransition><Intake /></PageTransition></AdminRoute>} />
          <Route path="/process/scoring" element={<AdminRoute><PageTransition><Scoring /></PageTransition></AdminRoute>} />
          <Route path="/process/approval" element={<AdminRoute><PageTransition><Approval /></PageTransition></AdminRoute>} />
          <Route path="/process/contracts" element={<AdminRoute><PageTransition><Contracts /></PageTransition></AdminRoute>} />

          {/* Legacy redirects */}
          <Route path="/workflow" element={<Navigate to="/process/intake" replace />} />
          <Route path="/loan-requests" element={<Navigate to="/process/intake" replace />} />
          <Route path="/applicants" element={<Navigate to="/process/scoring" replace />} />
          <Route path="/applicants/:id" element={<Navigate to="/process/scoring" replace />} />

          {/* Admin-only routes */}
          <Route path="/clients" element={<AdminRoute><PageTransition><Clients /></PageTransition></AdminRoute>} />
          <Route path="/clients/:id" element={<AdminRoute><PageTransition><ClientDetail /></PageTransition></AdminRoute>} />
          {/* Admin náhled klientského portálu — read-only, sdílí MyLoans komponentu, detekce přes useParams */}
          <Route path="/clients/:id/portal-preview" element={<AdminRoute><PageTransition><MyLoans /></PageTransition></AdminRoute>} />
          <Route path="/loans" element={<AdminRoute><PageTransition><Loans /></PageTransition></AdminRoute>} />
          <Route path="/loans/:id" element={<AdminRoute><PageTransition><LoanDetail /></PageTransition></AdminRoute>} />
          <Route path="/payments" element={<AdminRoute><PageTransition><Payments /></PageTransition></AdminRoute>} />
          <Route path="/bank-transactions" element={<AdminRoute><PageTransition><BankTransactions /></PageTransition></AdminRoute>} />
          <Route path="/settings" element={<AdminRoute><PageTransition><Settings /></PageTransition></AdminRoute>} />
          <Route path="/users" element={<AdminRoute><PageTransition><UserManagement /></PageTransition></AdminRoute>} />
          <Route path="/todos" element={<AdminRoute><PageTransition><SystemTodos /></PageTransition></AdminRoute>} />
          <Route path="/system-todos" element={<Navigate to="/todos" replace />} />
          <Route path="/system-health" element={<AdminRoute><PageTransition><SystemHealth /></PageTransition></AdminRoute>} />
          <Route path="/audit-log" element={<AdminRoute><PageTransition><AuditLog /></PageTransition></AdminRoute>} />
          <Route path="/decision-audit" element={<AdminRoute><PageTransition><DecisionAudit /></PageTransition></AdminRoute>} />
          <Route path="/sandbox" element={<AdminRoute><PageTransition><DecisionSandbox /></PageTransition></AdminRoute>} />
          <Route path="/ai-analysis-sandbox" element={<AdminRoute><PageTransition><AiAnalysisSandbox /></PageTransition></AdminRoute>} />
          <Route path="/rules" element={<AdminRoute><PageTransition><RulesManagement /></PageTransition></AdminRoute>} />
          <Route path="/capital" element={<AdminRoute><PageTransition><CapitalOverview /></PageTransition></AdminRoute>} />
          <Route path="/capital/sources" element={<AdminRoute><PageTransition><CapitalSources /></PageTransition></AdminRoute>} />
          <Route path="/capital/sources/:id" element={<AdminRoute><PageTransition><CapitalSourceDetail /></PageTransition></AdminRoute>} />
          <Route path="/capital/investments" element={<AdminRoute><PageTransition><CapitalInvestments /></PageTransition></AdminRoute>} />
          {/* Payouts page konsolidovány do Investments (inline payout summary v každé kartě) */}
          <Route path="/capital/payouts" element={<Navigate to="/capital/investments" replace />} />

          {/* Investor portal */}
          <Route path="/investor-portal" element={<InvestorRoute><PageTransition><InvestorPortal /></PageTransition></InvestorRoute>} />

          {/* Finance & Analytika: Přehled · Výsledky · Portfolio klientů (Kapitál je v sekci Kapitál) */}
          <Route path="/finance" element={<AdminRoute><PageTransition><Finance /></PageTransition></AdminRoute>} />
          <Route path="/financials" element={<AdminRoute><PageTransition><Financials /></PageTransition></AdminRoute>} />
          <Route path="/portfolio" element={<AdminRoute><PageTransition><Portfolio /></PageTransition></AdminRoute>} />
          {/* Přesměrování zrušených stránek (staré záložky a odkazy z notifikací) */}
          <Route path="/dashboard" element={<Navigate to="/finance" replace />} />
          <Route path="/operational" element={<Navigate to="/finance" replace />} />
          <Route path="/risks-liquidity" element={<Navigate to="/finance" replace />} />
          <Route path="/cashflow" element={<Navigate to="/finance" replace />} />
          <Route path="/risk" element={<Navigate to="/finance" replace />} />
          <Route path="/predictions" element={<Navigate to="/finance" replace />} />
          <Route path="/company-growth" element={<Navigate to="/portfolio" replace />} />
          <Route path="/client-analytics" element={<Navigate to="/portfolio" replace />} />
          <Route path="/operations" element={<Navigate to="/portfolio" replace />} />
          <Route path="/exec" element={<Navigate to="/portfolio" replace />} />
          <Route path="/reports" element={<Navigate to="/loans" replace />} />

          {/* User routes */}
          <Route path="/user-dashboard" element={<UserRoute><PageTransition><UserDashboard /></PageTransition></UserRoute>} />
          <Route path="/profile" element={<ProfileRoute><PageTransition><UserProfile /></PageTransition></ProfileRoute>} />
          <Route path="/my-loans" element={<UserRoute><PageTransition><MyLoans /></PageTransition></UserRoute>} />
          <Route path="/new-loan-request" element={<UserRoute><PageTransition><NewLoanRequest /></PageTransition></UserRoute>} />
          <Route path="/contracts/sign/:id" element={<UserRoute><PageTransition><ContractSign /></PageTransition></UserRoute>} />

          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}
